import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Scissors, Pizza, ArrowUpRight, Download } from "lucide-react";
import MaskedHeading from "@/components/MaskedHeading";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { hub } from "@/data/demoData";

function LogoMark({ className = "h-9 w-9" }) {
    return (
        <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
            <rect width="64" height="64" fill="#111111" />
            <path d="M18 20 L32 46 L46 20" stroke="#FFFFFF" strokeWidth="5" strokeLinecap="square" fill="none" />
            <circle cx="46" cy="13" r="3.5" fill="#E30613" />
        </svg>
    );
}

function AppleIcon({ className = "" }) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
            <path d="M16.365 1.43c0 1.14-.46 2.2-1.21 3.02-.9 1-2.37 1.77-3.57 1.68-.15-1.1.42-2.27 1.14-3.05.83-.92 2.26-1.62 3.64-1.65zM20.94 17.1c-.57 1.3-.84 1.88-1.57 3.03-1.02 1.6-2.46 3.6-4.25 3.61-1.59.02-2-1.03-4.16-1.02-2.16.01-2.61 1.04-4.2 1.03-1.79-.02-3.16-1.82-4.18-3.42C.03 16.13-.28 11.3 1.5 8.63c1.26-1.87 3.25-2.97 5.12-2.97 1.9 0 3.1 1.04 4.67 1.04 1.53 0 2.46-1.04 4.66-1.04 1.66 0 3.42.9 4.67 2.46-4.1 2.25-3.44 8.1.32 8.98z" />
        </svg>
    );
}

function AndroidIcon({ className = "" }) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
            <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v6c0 .83.67 1.5 1.5 1.5S5 16.33 5 15.5v-6C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v6c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-6c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.06-1.06c.19-.19.19-.51 0-.7-.19-.19-.51-.19-.7 0l-1.2 1.2C13.63 1.23 12.35 1 11 1s-2.63.23-3.19.6L6.61.4c-.19-.19-.51-.19-.7 0-.19.19-.19.51 0 .7l1.06 1.06C5.37 3.12 4.25 5.04 4.25 7h13.5c0-1.96-1.12-3.88-2.72-4.84zM8.75 5.5c-.41 0-.75-.34-.75-.75s.34-.75.75-.75.75.34.75.75-.34.75-.75.75zm6.5 0c-.41 0-.75-.34-.75-.75s.34-.75.75-.75.75.34.75.75-.34.75-.75.75z" />
        </svg>
    );
}

function InstallButton() {
    const [promptEvent, setPromptEvent] = useState(null);
    useEffect(() => {
        const handler = (e) => {
            e.preventDefault();
            setPromptEvent(e);
        };
        window.addEventListener("beforeinstallprompt", handler);
        return () => window.removeEventListener("beforeinstallprompt", handler);
    }, []);
    if (!promptEvent) return null;
    return (
        <button
            data-testid="app-install-btn"
            onClick={async () => {
                promptEvent.prompt();
                await promptEvent.userChoice;
                setPromptEvent(null);
            }}
            className="inline-flex items-center gap-2 bg-swiss-red px-5 py-3 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-300 hover:bg-ink"
        >
            <Download className="h-4 w-4" />
            Installa ora
        </button>
    );
}

function QrCard({ platform, icon, steps, testid, qrTestid, children }) {
    const appUrl = window.location.origin;
    return (
        <div data-testid={testid} className="flex h-full flex-col border border-white/15 bg-white p-6 text-ink sm:p-8">
            <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    {icon}
                    <span className="text-lg font-bold uppercase tracking-tight">{platform}</span>
                </div>
                <span className="bg-ink px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-white">App</span>
            </div>
            <div className="flex flex-1 flex-col items-center gap-6 sm:flex-row sm:items-start">
                <div className="flex shrink-0 flex-col items-center gap-2">
                    <div data-testid={qrTestid} className="border-2 border-ink p-2">
                        <QRCodeSVG value={appUrl} size={116} bgColor="#FFFFFF" fgColor="#111111" level="M" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-swiss-gray">{appUrl.replace(/^https?:\/\//, "")}</span>
                </div>
                <ol className="flex-1 space-y-3">
                    {steps.map((step, i) => (
                        <li key={i} className="flex items-start gap-3">
                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center bg-swiss-red text-[11px] font-bold text-white">
                                {i + 1}
                            </span>
                            <span className="text-sm leading-snug text-ink/85">{step}</span>
                        </li>
                    ))}
                    {children}
                </ol>
            </div>
        </div>
    );
}

function AppSection() {
    return (
        <section id="app" data-testid="app-download-section" className="mx-auto max-w-7xl px-6 pb-20 sm:px-12">
            <div className="mb-10 flex flex-col items-center text-center">
                <Reveal y={16}>
                    <p className="mb-4 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.4em] text-swiss-red">
                        <span className="inline-block h-px w-8 bg-swiss-red" />
                        App Vetrina
                        <span className="inline-block h-px w-8 bg-swiss-red" />
                    </p>
                </Reveal>
                <Reveal delay={0.1}>
                    <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">Scarica l'app sul telefono</h2>
                    <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-white/60 sm:text-base">
                        Vetrina si installa come una vera app: scansiona il QR con il telefono e aggiungila alla schermata Home.
                    </p>
                </Reveal>
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
                <Reveal>
                    <QrCard
                        platform="Android"
                        icon={<AndroidIcon className="h-7 w-7 text-ink" />}
                        steps={["Scansiona il QR con la fotocamera del telefono", "Apri il link in Chrome", `Tocca "Installa app" dal menu di Chrome`]}
                        testid="app-card-android"
                        qrTestid="qr-android"
                    >
                        <li className="pt-2">
                            <InstallButton />
                        </li>
                    </QrCard>
                </Reveal>
                <Reveal delay={0.1}>
                    <QrCard
                        platform="iPhone · iOS"
                        icon={<AppleIcon className="h-7 w-7 text-ink" />}
                        steps={["Scansiona il QR con la fotocamera iPhone", "Il sito si apre in Safari", `Tocca Condividi, poi "Aggiungi alla schermata Home"`]}
                        testid="app-card-ios"
                        qrTestid="qr-ios"
                    />
                </Reveal>
            </div>
        </section>
    );
}

function HubCard({ card, index }) {
    const Icon = card.icon === "scissors" ? Scissors : Pizza;
    return (
        <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 + index * 0.18, ease: [0.22, 1, 0.36, 1] }}
        >
            <Link
                to={card.to}
                data-testid={`hub-card-${card.to === "/salone" ? "salon" : "pizzeria"}`}
                className="group relative block h-[420px] overflow-hidden border border-white/15 transition-colors duration-500 hover:border-swiss-red sm:h-[520px]"
            >
                <img
                    src={card.bg}
                    alt={card.title}
                    loading={index === 0 ? "eager" : "lazy"}
                    className="absolute inset-0 h-full w-full object-cover grayscale transition-all duration-[1200ms] ease-out group-hover:scale-105 group-hover:grayscale-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/40 to-transparent" />
                <div className="relative z-10 flex h-full flex-col justify-between p-6 sm:p-10">
                    <div className="flex items-start justify-between">
                        <span className="bg-swiss-red px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.3em] text-white sm:text-xs">
                            {card.badge}
                        </span>
                        <span className="flex h-12 w-12 items-center justify-center border border-white/40 text-white transition-colors duration-500 group-hover:bg-swiss-red group-hover:border-swiss-red sm:h-14 sm:w-14">
                            <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                        </span>
                    </div>
                    <div>
                        <h2 className="text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-[2.75rem]">
                            {card.title}
                        </h2>
                        <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/70 sm:text-base">{card.description}</p>
                        <span className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-white sm:text-sm">
                            <span className="inline-block h-2 w-2 bg-swiss-red" />
                            {card.action}
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                        </span>
                    </div>
                </div>
            </Link>
        </motion.div>
    );
}

export default function HomePage() {
    return (
        <main data-testid="home-page" className="min-h-screen bg-[#111111] text-white">
            <header className="relative z-20 flex items-center justify-between border-b border-white/10 px-6 py-5 sm:px-12">
                <div className="flex items-center gap-3">
                    <LogoMark />
                    <span className="text-lg font-bold tracking-tight text-white">VETRINA</span>
                </div>
                <span className="border border-white/25 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.3em] text-white/70">
                    Catalogo Demo · Svizzera
                </span>
            </header>

            <section className="relative z-10 mx-auto max-w-6xl px-6 pb-14 pt-12 text-center sm:px-12 sm:pt-20">
                <Reveal y={16}>
                    <p className="mb-6 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.4em] text-swiss-red">
                        <span className="inline-block h-px w-8 bg-swiss-red" />
                        Demo commerciale
                        <span className="inline-block h-px w-8 bg-swiss-red" />
                    </p>
                </Reveal>
                <MaskedHeading
                    lines={["Scegli la", "tua attività"]}
                    delay={0.25}
                    className="text-6xl font-bold leading-[1.02] tracking-tight text-white sm:text-7xl lg:text-8xl"
                />
                <Reveal delay={0.75}>
                    <p className="mx-auto mt-7 max-w-xl text-sm leading-relaxed text-white/60 sm:text-base">
                        Catalogo interattivo di siti web professionali su misura per attività locali ad alta conversione.
                    </p>
                </Reveal>
            </section>

            <section className="relative z-10 mx-auto grid max-w-7xl gap-6 px-6 pb-16 sm:px-12 lg:grid-cols-2 lg:gap-8">
                {hub.cards.map((card, i) => (
                    <HubCard key={card.to} card={card} index={i} />
                ))}
            </section>

            <div className="relative z-10 text-white">
                <Marquee items={hub.marquee} className="border-white/15" />
            </div>

            <AppSection />

            <footer className="relative z-10 flex flex-col items-center gap-2 border-t border-white/10 px-6 py-10 text-center">
                <p className="text-xs uppercase tracking-[0.3em] text-white/40">Vetrina · Demo commerciale · Svizzera</p>
                <p className="text-xs text-white/30">Siti reali su misura per la tua attività</p>
            </footer>
        </main>
    );
}
